import classnames from "classnames";
import ReactTooltip from "components/ReactTooltip";
import { CATEGORIES, CATEGORIES_COLORS } from "const";
import { memo, ReactNode, useMemo } from "react";

interface IDotProps {
  classificationName?: string;
  classificationDescription?: string;
  tooltipId?: string;
  onClick?: () => void;
  className?: string;
  styleLess?: boolean;
  variant?: "filled" | "outline" | "tag";
  children: ReactNode;
  classNames?: {
    contend?: string;
    text?: string;
  };
}

function BorderBadge({
  classificationName = "",
  classificationDescription = "",
  tooltipId,
  className,
  children,
  classNames,
  styleLess = false,
  variant = "filled",
  ...rest
}: IDotProps) {
  const badgeColor = useMemo(() => {
    // For tag variant, don't use category colors
    if (variant === "tag") {
      return "";
    }

    // Check for matching category color
    if (CATEGORIES[classificationName as keyof typeof CATEGORIES]) {
      return CATEGORIES[classificationName as keyof typeof CATEGORIES];
    }

    // For outline variant without a matching category, return empty
    if (variant === "outline") {
      return "";
    }

    // For filled variant, try localStorage or generate random color
    const badgeColors = JSON.parse(localStorage.getItem("badgeColors") || "{}");
    const storedColor = badgeColors[classificationName];
    if (storedColor) {
      return storedColor;
    } else {
      const randomColor =
        CATEGORIES_COLORS[Math.floor(Math.random() * CATEGORIES_COLORS.length)];

      const updatedBadgeColors = {
        ...badgeColors,
        [classificationName]: randomColor,
      };
      localStorage.setItem("badgeColors", JSON.stringify(updatedBadgeColors));
      return randomColor;
    }
  }, [classificationName, variant]);

  // Documentary Editorial styling based on variant
  const getVariantStyles = () => {
    if (styleLess) return {};

    switch (variant) {
      case "outline":
        return {
          background: "transparent",
          border: "1px solid #D6D3D1",
          color: "#57534E",
        };
      case "tag":
        return {
          background: "#F5F5F4",
          border: "1px solid #E7E5E4",
          color: "#78716C",
        };
      case "filled":
      default:
        return {}; // Uses badgeColor class
    }
  };

  const variantStyles = getVariantStyles();

  return (
    <div
      className={classnames(
        {
          "rounded-full": !styleLess,
        },
        className,
        variant === "filled" && !styleLess ? badgeColor : ""
      )}
      style={variant !== "filled" ? variantStyles : undefined}
      {...rest}
    >
      <div
        className={classnames(
          {
            "relative flex items-center justify-center py-1.5 px-3 rounded-full":
              !styleLess,
          },
          classNames?.contend
        )}
      >
        <span
          className={classnames(
            {
              "text-xs font-medium line-clamp-1 text-center whitespace-nowrap":
                !styleLess,
              "text-stone-700": !styleLess && variant === "filled",
            },
            classNames?.text
          )}
          style={{
            fontFamily: "'Source Sans 3', sans-serif",
            ...(variant !== "filled" ? { color: variantStyles.color } : {}),
          }}
          data-tooltip-id={classificationName + tooltipId}
        >
          {children}
        </span>
        {classificationDescription && (
          <ReactTooltip
            id={classificationName + tooltipId}
            content={classificationDescription}
            className={classnames(
              "white-tooltip font-semibold !rounded-md !p-2 z-30 max-w-[200px]",
              className
            )}
          />
        )}
      </div>
    </div>
  );
}
export default memo(BorderBadge);
